import React, { useState, useEffect, useCallback } from 'react';
import MetaTags from "react-meta-tags";
import ApiClient from '../../BaseUrl/ApiClient';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import { Card, CardHeader, CardBody, Col, Container, Row, Input, FormGroup, Modal, ModalBody } from 'reactstrap';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

function DealRequestList() {
  const [dealRequests, setDealRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    totalDocs: 0,
    limit: 10,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImages, setModalImages] = useState([]);

  // Searchable fields
  const searchParam = ['name', 'businessName'];

  // Fetch deal requests with optional search query
  const fetchDealRequests = async (page = 1, limit = pagination.limit, query = '') => {
    setIsLoading(true);
    try {
      const params = { page, limit, search: query }; // <-- ensure API expects 'search'
      const response = await ApiClient().get('/admin/listdealrequest/list', { params });
      const { responseData } = response.data;

      setDealRequests(responseData.dealRequest);
      setPagination(responseData.pagination);
    } catch (error) {
      toast.error("Failed to fetch deal requests.");
      if (error.response?.status === 401) {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("authUser");
        window.location.href = "/login";
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Debounced fetch
  const debouncedFetchDealRequests = useCallback(
    debounce((query, page, limit) => {
      fetchDealRequests(page, limit, query);
    }, 500),
    []
  );

  useEffect(() => {
    fetchDealRequests();
  }, []);

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    debouncedFetchDealRequests(query, 1, pagination.limit);
  };

  const handlePageChange = (newPage) => {
    fetchDealRequests(newPage, pagination.limit, searchQuery);
  };

  const handleLimitChange = (e) => {
    const newLimit = Number(e.target.value);
    setPagination(prev => ({ ...prev, limit: newLimit }));
    fetchDealRequests(1, newLimit, searchQuery);
  };

  const openModal = (images) => {
    setModalImages(images);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalImages([]);
  };

  // Filter dealRequests based on searchParam if API doesn't handle search
  const filteredDealRequests = dealRequests.filter((deal) =>
    searchParam.some((param) =>
      deal[param]?.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  return (
    <div className="page-content">
      <Container fluid className="mt-5">
        <MetaTags>
          <title>Deal Request List</title>
        </MetaTags>
        <ToastContainer />
        <div className="auth-page-content custom-auth-page">
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <h2 className="card-title mb-0">Deal Requests</h2>
                  <h2 className="card-title mb-0">
                    Total Deal Requests: <span className="counter-value">{pagination.totalDocs}</span>
                  </h2>
                </CardHeader>

                <CardBody>
                  <div id="DealRequestList">
                    <Row className="g-3 justify-content-end">
                      <Col md={4}>
                        <FormGroup>
                          <div className="search-box ms-2 position-relative">
                            <Input
                              type="text"
                              className="form-control search"
                              placeholder="Search by name, business"
                              value={searchQuery}
                              onChange={handleSearchInputChange}
                            />
                            <i className="ri-search-line search-icon"></i>
                            {searchQuery && (
                              <i
                                className="ri-close-line clear-icon"
                                style={{
                                  position: "absolute",
                                  right: "10px",
                                  top: "50%",
                                  transform: "translateY(-50%)",
                                  cursor: "pointer",
                                  fontSize: "18px",
                                  color: "#666",
                                }}
                                onClick={() => {
                                  setSearchQuery("");
                                  fetchDealRequests(1, pagination.limit, '');
                                }}
                              />
                            )}
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>

                    <div className="table-responsive table-card mt-3 mb-1">
                      <table className="table align-middle table-nowrap" id="dealRequestTable">
                        <thead className="table-light">
                          <tr>
                            <th>Deal Photo</th>
                            <th>Name</th>
                            <th>Business Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredDealRequests.map((deal) => (
                            <tr key={deal._id}>
                              <td>
                                {deal.dealPhoto && deal.dealPhoto.length > 0 ? (
                                  <div
                                    style={{ position: 'relative', display: 'inline-block', cursor: 'pointer' }}
                                    onClick={() => openModal(deal.dealPhoto)}
                                  >
                                    <img
                                      src={deal.dealPhoto[0].url}
                                      alt="Deal"
                                      style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' }}
                                    />
                                    {deal.dealPhoto.length > 1 && (
                                      <div
                                        style={{
                                          position: 'absolute',
                                          bottom: 0,
                                          right: 0,
                                          backgroundColor: 'rgba(0,0,0,0.6)',
                                          color: 'white',
                                          fontSize: '12px',
                                          padding: '2px 4px',
                                          borderRadius: '4px'
                                        }}
                                      >
                                        +{deal.dealPhoto.length - 1}
                                      </div>
                                    )}
                                  </div>
                                ) : 'N/A'}
                              </td>
                              <td>{deal.name || 'N/A'}</td>
                              <td>{deal.businessName || 'N/A'}</td>
                              <td>{deal.email || 'N/A'}</td>
                              <td>{deal.phoneNumber || 'N/A'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {isLoading && dealRequests.length === 0 && (
                        <div className="text-center py-4">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <h5 className="mt-2">Loading Results...</h5>
                        </div>
                      )}

                      {!isLoading && dealRequests.length === 0 && (
                        <div className="noresult text-center py-4">
                          <lord-icon
                            src="https://cdn.lordicon.com/msoeawqm.json"
                            trigger="loop"
                            colors="primary:#121331,secondary:#08a88a"
                            style={{ width: '75px', height: '75px' }}
                          ></lord-icon>
                          <h5 className="mt-2">Sorry! No Result Found</h5>
                          <p className="text-muted mb-0">
                            We've searched for deal requests but did not find any matching results.
                          </p>
                        </div>
                      )}
                    </div>

                    {pagination.totalPages > 1 && (
                      <div className="d-flex justify-content-end mt-3">
                        <div className="pagination-wrap hstack gap-2">
                          <Link
                            className={`page-item pagination-prev ${pagination.page === 1 ? 'disabled' : ''}`}
                            to="#"
                            onClick={() => handlePageChange(pagination.page - 1)}
                          >
                            Previous
                          </Link>
                          <ul className="pagination listjs-pagination mb-0">
                            {Array.from({ length: pagination.totalPages }, (_, i) => (
                              <li key={i} className={`page-item ${pagination.page === i + 1 ? 'active' : ''}`}>
                                <Link className="page-link" to="#" onClick={() => handlePageChange(i + 1)}>
                                  {i + 1}
                                </Link>
                              </li>
                            ))}
                          </ul>
                          <Link
                            className={`page-item pagination-next ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}
                            to="#"
                            onClick={() => handlePageChange(pagination.page + 1)}
                          >
                            Next
                          </Link>
                        </div>

                        <select
                          className="form-select ms-2"
                          style={{ width: '100px' }}
                          value={pagination.limit}
                          onChange={handleLimitChange}
                        >
                          <option value="10">10</option>
                          <option value="20">20</option>
                          <option value="50">50</option>
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Modal for showing all images */}
                  <Modal isOpen={modalOpen} toggle={closeModal} size="lg" centered >
                    <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
                      <h5 className="mb-0">Deal Photos</h5>
                      <button
                        type="button"
                        className="btn-close"
                        aria-label="Close"
                        onClick={closeModal}
                      ></button>
                    </div>
                    <ModalBody>
                      <div className="d-flex flex-wrap gap-2">
                        {modalImages.map((img) => (
                          <img
                            key={img._id}
                            src={img.url}
                            alt="Deal"
                            style={{ width: '150px', height: '150px', objectFit: 'cover', borderRadius: '4px' }}
                          />
                        ))}
                      </div>
                    </ModalBody>
                  </Modal>

                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </Container>
    </div>
  );
}

export default DealRequestList;
